/**
 * Executes an async worker function across an array of items with a bounded concurrency pool.
 * Preserves result ordering and guarantees all items settle even if individual tasks fail.
 *
 * @param items Items to process
 * @param worker Async function invoked for each item
 * @param concurrency Maximum concurrent executions (default 5)
 */
export async function runWithConcurrency<T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
  concurrency = 5
): Promise<PromiseSettledResult<R>[]> {
  if (items.length === 0) {
    return [];
  }

  const effectiveConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let nextIndex = 0;

  const runners = Array.from({ length: effectiveConcurrency }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      try {
        const value = await worker(items[currentIndex], currentIndex);
        results[currentIndex] = { status: 'fulfilled', value };
      } catch (reason) {
        results[currentIndex] = { status: 'rejected', reason };
      }
    }
  });

  await Promise.all(runners);
  return results;
}
